import React from "react";
import Button from "src/Components/Button";
import DatePicker from "src/Components/DatePicker";
import StaticDropdown from "src/Components/Dropdown/StaticDropdown";
import TabBox from "src/Components/Tabbox/TabBox";
import { DropdownItem } from "src/Interfaces/dropdownInterfaces";
import {
  MarketHistoryCategory,
  MarketHistoryRow,
  MarketHistorySearchParams,
} from "src/Interfaces/marketHistory";
import { Header } from "src/Interfaces/TableInterfaces";
import { getMarketHistoryOptions, getMarketHistoryTable, getMarketHistoryExcel } from "src/Requests/marketHistory";
import TableTemplate, { ITable, ITableUI } from "src/Tables/TableTemplate/tableTemplate";
import { CommonTableUI } from "src/Tables/TableTemplate/common";
import HistoryOverview from "./HistoryOverview";
import FileDownloader from "src/Components/FileDownloader";
import { packetErrorHandler } from "src/Utils/requestUtils";
import { FileExcelOutlined } from "@ant-design/icons";

const tabs: { index: number; title: string; category: MarketHistoryCategory }[] = [
  { index: 0, title: "تاریخچه اوراق", category: "securities" },
  { index: 1, title: "تاریخچه صندوق", category: "funds" },
  { index: 2, title: "تاریخچه شاخص", category: "indices" },
  { index: 3, title: "تاریخچه اختیار", category: "options" },
];
const firstDate = "2024-03-20";
const numberFormatter = new Intl.NumberFormat("en-US", { maximumFractionDigits: 20 });
const volumeAverageFormatter = new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 });
const volumeAverageFields = new Set([
  "averageVolume5", "averageVolume20", "averageAskVolume", "averageBidVolume",
  "averageAskVolume5", "averageBidVolume5",
]);
const numericFields = new Set([
  "tradeCount", "volume", "value", "closing", "first", "last", "low", "high", "redemptionNav",
  "averageVolume5", "averageVolume20", "averageValue5", "averageValue20",
  "averageAskVolume", "averageBidVolume",
  "averageAskVolume5", "averageBidVolume5",
]);

function todayInTehran() {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Tehran", year: "numeric", month: "2-digit", day: "2-digit",
  }).format(new Date());
}

function initialSearch(): MarketHistorySearchParams {
  const today = todayInTehran();
  const from = new Date(`${today}T00:00:00Z`);
  from.setUTCDate(from.getUTCDate() - 30);
  return { key: "", fromDate: from.toISOString().slice(0, 10), toDate: today };
}

function renderHistoryCell(row: MarketHistoryRow, header: Header) {
  const field = header.headerName.charAt(0).toLowerCase() + header.headerName.slice(1);
  if (!numericFields.has(field)) return undefined;
  const value = row[field as keyof MarketHistoryRow];
  const formatter = volumeAverageFields.has(field) ? volumeAverageFormatter : numberFormatter;
  return <span dir="ltr">{typeof value === "number" ? formatter.format(value) : "—"}</span>;
}

function HistoryTable(props: ITableUI<MarketHistoryRow>) {
  return <CommonTableUI {...props} renderCell={renderHistoryCell} tableBoxClassName="!max-h-[65vh]" />;
}

function HistoryTab({ category }: { category: MarketHistoryCategory }) {
  const [filters, setFilters] = React.useState(initialSearch);
  const [search, setSearch] = React.useState<MarketHistorySearchParams | null>(null);
  const [options, setOptions] = React.useState<DropdownItem[]>([]);
  const [optionsLoading, setOptionsLoading] = React.useState(true);
  const [optionsError, setOptionsError] = React.useState(false);
  const [validationError, setValidationError] = React.useState("");
  const [count, setCount] = React.useState<number>();

  React.useEffect(() => {
    const controller = new AbortController();
    setOptionsLoading(true);
    setOptionsError(false);
    getMarketHistoryOptions(category, controller.signal)
      .then((items) => {
        if (controller.signal.aborted) return;
        setOptions(items);
        if (items.length) {
          const defaultOption = category === "indices"
            ? items.find(item => item.id === "index-1-32097828799138957") ?? items[0]
            : items[0];
          const selected = { ...initialSearch(), key: String(defaultOption.id) };
          setFilters(selected);
          setSearch(selected);
        }
      })
      .catch(error => {
        if (controller.signal.aborted) return;
        setOptionsError(true);
        packetErrorHandler(error);
      })
      .finally(() => { if (!controller.signal.aborted) setOptionsLoading(false); });
    return () => controller.abort();
  }, [category]);

  const getData = React.useCallback<ITable<MarketHistorySearchParams, MarketHistoryRow>["getDataTable"]>(
    (input, offset, pageSize, sorting, _, axiosOptions) =>
      getMarketHistoryTable(category, input, offset, pageSize, sorting, axiosOptions),
    [category],
  );

  const changeFilter = (name: string, value: string) => {
    setFilters((previous) => ({ ...previous, [name]: value }));
    setValidationError("");
  };

  const submit = () => {
    if (optionsLoading || optionsError || !options.length) return;
    if (!filters.key) {
      setValidationError("یک نماد یا شاخص انتخاب کنید.");
      return;
    }
    if (!filters.fromDate || !filters.toDate || filters.fromDate < firstDate ||
        filters.fromDate > filters.toDate || filters.toDate > todayInTehran()) {
      setValidationError("بازهٔ معتبر بین ۱۴۰۳/۰۱/۰۱ و امروز انتخاب کنید؛ تاریخ شروع نباید بعد از تاریخ پایان باشد.");
      return;
    }
    setValidationError("");
    setCount(undefined);
    setSearch({ ...filters });
  };

  return (
    <div className="w-full flex flex-col gap-4">
      <div className="flex flex-wrap items-end gap-4">
        <div className="w-64">
          <StaticDropdown
            dropdown={{ name: "key", placeholder: category === "indices" ? "شاخص" : "نماد", extraLabel: true, disabled: optionsLoading || optionsError || !options.length }}
            options={options}
            value={search ? filters.key || null : null}
            onChange={(_, value) => changeFilter("key", String(value ?? ""))}
          />
        </div>
        <div className="w-48 flex flex-col gap-1"><span className="text-[10px]">از تاریخ</span><DatePicker name="fromDate" label="از تاریخ" value={filters.fromDate} onChange={changeFilter} /></div>
        <div className="w-48 flex flex-col gap-1"><span className="text-[10px]">تا تاریخ</span><DatePicker name="toDate" label="تا تاریخ" value={filters.toDate} onChange={changeFilter} /></div>
        <div className="w-28"><Button text="جستجو" variant="contained" onClick={() => submit()} disabled={optionsLoading || optionsError || !options.length} /></div>
      </div>
      {validationError ? <p role="alert" className="text-red-600 text-sm">{validationError}</p> : null}
      {!optionsLoading && !optionsError && !options.length ? <p className="text-sm text-gray-500">نماد یا شاخصی برای نمایش موجود نیست.</p> : null}
      {search ?
        <HistoryOverview key={`${category}:${search.key}:${search.fromDate}:${search.toDate}`} category={category} search={search} /> : null}
      {search ? <div className="mx-auto w-fit max-w-full flex flex-col gap-3">
        <div className="flex items-center justify-end px-3">
          <FileDownloader getFile={() => getMarketHistoryExcel(category, search)} text={
            <button type="button" aria-label="دانلود اکسل جدول" title="دانلود اکسل جدول"
              className="flex h-10 w-10 items-center justify-center rounded-full border border-sky-500 bg-white text-xl text-sky-500 hover:bg-sky-50">
              <FileExcelOutlined />
            </button>
          } />
        </div>
        <TableTemplate<MarketHistorySearchParams, MarketHistoryRow>
        getDataTable={getData}
        searchParameters={search}
        pagesize={500}
        withSort
        TableUI={HistoryTable}
        onResponse={(response) => setCount(response.count)}
        onFetchError={() => { setCount(undefined); return false; }}
      /></div> : null}
      {count === 0 ? <p className="text-center text-sm text-gray-500">در بازهٔ انتخابی سابقه‌ای موجود نیست.</p> : null}
    </div>
  );
}

export default function MarketHistory() {
  const [tabIndex, setTabIndex] = React.useState(0);
  const category = tabs[tabIndex].category;
  return (
    <div className="w-full p-5 flex flex-col gap-5" dir="rtl">
      <div className="w-fit"><TabBox activeTab={tabIndex} onChange={(_, index) => setTabIndex(index)}>{tabs}</TabBox></div>
      {<HistoryTab key={category} category={category} />}
    </div>
  );
}
